// ── LONG-FORM COPY: ABOUT, CONTACT, PRIVACY, AI ──────────────────────────────
// Kept out of i18n/index.js deliberately. That file is a flat map of short UI
// strings; these are paragraphs, and threading them through it would make both
// harder to read. Same shape though — one object per locale, looked up by the
// active language — so it behaves identically at the call site.
//
// ══ ABOUT THE PRIVACY TEXT ═══════════════════════════════════════════════════
// WRITTEN FROM WHAT THE CODE ACTUALLY DOES, not from a template. Every claim
// below was checked against the source:
//
//   · Firebase Authentication — email, Google and phone sign-in
//     (src/context/AuthContext.jsx, src/firebase/phoneAuth.js)
//   · Cloud Firestore — profiles, projects, gallery items, chats, likes
//   · Uploaded images, stored as compressed blobs (src/utils/imageCompress.js)
//   · Replicate, for AI image generation (functions/ — flux models)
//   · Firebase Cloud Messaging — push notifications, only once permitted
//     (src/hooks/usePushNotifications.js)
//   · Google reCAPTCHA — on phone sign-in only (src/firebase/phoneAuth.js)
//
// IT IS NOT LEGAL ADVICE AND HAS NOT BEEN REVIEWED BY A LAWYER. It is an
// accurate description of the system's data handling, which is the part an
// engineer can be responsible for. Armenian law (and GDPR, if you take EU
// users) may require disclosures that are not here — a retention schedule, a
// named data controller, a supervisory-authority contact. Have it reviewed
// before relying on it.

export const CONTACT = {
  phones: ['+374 44 588 938', '+374 41 588 938'],
  // tel: links need the bare international form, no spaces.
  phonesHref: ['+37444588938', '+37441588938'],
};

const en = {
  about_title: 'About Tunify',
  about_lead:
    'Tunify is a home design tool. You sketch a floor plan, furnish the rooms in 3D, '
    + 'and turn any view into a photorealistic image — in a browser, with nothing to install.',
  about_body: [
    'It began as a simple question: why is it so hard to see what a room will look like before '
    + 'you commit to it? Furniture is expensive, renovation is slow, and a decision made from a '
    + 'shop floor is a decision made half-blind.',
    'So Tunify puts the room on your screen first. Draw the walls to real measurements, place '
    + 'doors and windows, drop in furniture, then walk through it in 3D. When you want to see it '
    + 'finished, the AI renders it as a photograph.',
    'Around that sit the two things a real project needs: interior designers who publish their '
    + 'work here and take commissions, and shops selling the furniture, materials and finishes '
    + 'you have just placed in your plan.',
  ],
  about_contact_title: 'Contact us',
  about_contact_sub: 'Questions, problems, or working with us — call either number.',

  ai_title: 'About the AI renders',
  ai_body:
    'AI image generation is not predictable, and it will not be right every time. A render may '
    + 'change details you wanted kept, misread a material, or produce something that simply looks '
    + 'wrong — and the same settings can give a different result on a second attempt. Treat every '
    + 'render as an impression of a design, not a specification of it. Do not order materials, '
    + 'cut anything, or sign off on work from an AI image alone: check the measurements in your '
    + 'own plan, and confirm finishes against real samples.',
  ai_short: 'AI renders are approximations. Results vary between attempts, and details can come out wrong — check measurements in your plan before relying on one.',

  privacy_title: 'Privacy Policy',
  privacy_intro:
    'This explains what Tunify collects, why, and who else sees it. It describes how the '
    + 'product actually works rather than restating the law.',
  privacy_sections: [
    ['What we collect',
      'Your account details — an email address, a phone number, or a Google account, depending on '
      + 'how you signed up — together with the name and profile picture you choose. If you publish '
      + 'as a designer or a shop we also hold what you put on that public profile: your description, '
      + 'location, styles, contact links and prices.'],
    ['What you create',
      'Floor plans, 3D designs, uploaded photographs, product listings, comments and messages are '
      + 'stored so you can come back to them. Anything you publish or share by link is visible to '
      + 'others; anything you do not is visible only to you.'],
    ['Messages',
      'Conversations are stored so both people can read them later. Message text is checked '
      + 'automatically against a list of blocked words before it is sent; that check runs in your '
      + 'browser and no message is sent anywhere for it.'],
    ['AI image generation',
      'When you generate a render, the source image and your chosen settings are sent to Replicate, '
      + 'which runs the model and returns the result. Do not upload images you are not willing to '
      + 'process on a third-party service.'],
    ['Who processes your data',
      'Google Firebase provides authentication, the database, file storage and push notifications. '
      + 'Replicate runs the AI image models. Google reCAPTCHA verifies phone sign-in. Each of these '
      + 'receives only what its job needs.'],
    ['Notifications',
      'Push notifications are sent only after you allow them in your browser, and you can withdraw '
      + 'that permission at any time in your browser settings.'],
    ['What we do not do',
      'We do not sell your personal data, and we do not run third-party advertising trackers.'],
    ['Your choices',
      'You can edit or delete your projects, listings and published work from within the app, and '
      + 'change or hide your public profile at any time. To have your account and its data removed, '
      + 'contact us on either number below.'],
    ['Changes',
      'If this policy changes materially we will update this page. Continuing to use Tunify after '
      + 'a change means you accept the current version.'],
  ],
  privacy_contact: 'Questions about any of this? Call us:',
  legal_reviewed_note:
    'This page describes how Tunify handles data. It is not legal advice and has not been reviewed '
    + 'by a lawyer.',
};

const ru = {
  about_title: 'О Tunify',
  about_lead:
    'Tunify — инструмент для дизайна дома. Вы чертите планировку, обставляете комнаты в 3D '
    + 'и превращаете любой вид в фотореалистичное изображение — прямо в браузере, без установки.',
  about_body: [
    'Всё началось с простого вопроса: почему так сложно увидеть комнату до того, как в неё вложены '
    + 'деньги? Мебель дорогая, ремонт долгий, а решение, принятое в магазине, принимается наполовину вслепую.',
    'Поэтому Tunify сначала показывает комнату на экране. Начертите стены в реальных размерах, '
    + 'поставьте двери и окна, расставьте мебель и пройдитесь по ней в 3D. А когда захотите увидеть '
    + 'результат — ИИ отрисует его как фотографию.',
    'Вокруг этого — то, без чего не обходится настоящий проект: дизайнеры интерьера, которые '
    + 'публикуют здесь свои работы и берут заказы, и магазины с мебелью, материалами и отделкой, '
    + 'которые вы только что расставили в плане.',
  ],
  about_contact_title: 'Связаться с нами',
  about_contact_sub: 'Вопросы, проблемы или сотрудничество — звоните на любой номер.',

  ai_title: 'Об ИИ-визуализациях',
  ai_body:
    'Генерация изображений ИИ непредсказуема и не всегда даёт верный результат. Визуализация может '
    + 'изменить детали, которые вы хотели сохранить, неверно передать материал или просто выглядеть '
    + 'неправильно — и одни и те же настройки со второй попытки дадут другой результат. Считайте '
    + 'визуализацию впечатлением от проекта, а не его спецификацией. Не заказывайте материалы, '
    + 'ничего не режьте и не утверждайте работы только по изображению ИИ: сверяйте размеры по '
    + 'своему плану, а отделку — по реальным образцам.',
  ai_short: 'ИИ-визуализации приблизительны. Результат меняется от попытки к попытке, детали могут выйти неверно — сверяйте размеры по своему плану.',

  privacy_title: 'Политика конфиденциальности',
  privacy_intro:
    'Здесь описано, какие данные собирает Tunify, зачем и кто ещё их видит. Это описание того, '
    + 'как продукт работает на самом деле, а не пересказ закона.',
  privacy_sections: [
    ['Что мы собираем',
      'Данные вашей учётной записи — адрес email, номер телефона или аккаунт Google, в зависимости '
      + 'от способа регистрации, — вместе с выбранным вами именем и фотографией профиля. Если вы '
      + 'публикуетесь как дизайнер или магазин, мы также храним содержимое публичного профиля: '
      + 'описание, местоположение, стили, контакты и цены.'],
    ['Что вы создаёте',
      'Планировки, 3D-проекты, загруженные фотографии, товары, комментарии и сообщения сохраняются, '
      + 'чтобы вы могли к ним вернуться. Всё опубликованное или отправленное по ссылке видно другим; '
      + 'остальное видно только вам.'],
    ['Сообщения',
      'Переписка сохраняется, чтобы оба собеседника могли её прочитать позже. Текст сообщения '
      + 'автоматически проверяется по списку запрещённых слов перед отправкой; проверка выполняется '
      + 'в вашем браузере, и сообщение для неё никуда не отправляется.'],
    ['Генерация изображений ИИ',
      'При создании визуализации исходное изображение и выбранные настройки отправляются в Replicate, '
      + 'который запускает модель и возвращает результат. Не загружайте изображения, которые вы не '
      + 'готовы обрабатывать на стороннем сервисе.'],
    ['Кто обрабатывает данные',
      'Google Firebase обеспечивает аутентификацию, базу данных, хранение файлов и push-уведомления. '
      + 'Replicate запускает модели генерации изображений. Google reCAPTCHA проверяет вход по телефону. '
      + 'Каждый из них получает только то, что нужно для его задачи.'],
    ['Уведомления',
      'Push-уведомления отправляются только после того, как вы разрешите их в браузере, и вы можете '
      + 'отозвать это разрешение в любой момент в настройках браузера.'],
    ['Чего мы не делаем',
      'Мы не продаём ваши персональные данные и не используем сторонние рекламные трекеры.'],
    ['Ваши возможности',
      'Вы можете изменить или удалить свои проекты, товары и опубликованные работы прямо в приложении, '
      + 'а также изменить или скрыть публичный профиль. Чтобы удалить учётную запись и её данные, '
      + 'позвоните нам по одному из номеров ниже.'],
    ['Изменения',
      'При существенных изменениях мы обновим эту страницу. Продолжая пользоваться Tunify после '
      + 'изменения, вы принимаете действующую версию.'],
  ],
  privacy_contact: 'Есть вопросы? Позвоните нам:',
  legal_reviewed_note:
    'На этой странице описано, как Tunify обращается с данными. Это не юридическая консультация, '
    + 'и текст не проверялся юристом.',
};

const hy = {
  about_title: 'Tunify-ի մասին',
  about_lead:
    'Tunify-ը տան դիզայնի գործիք է։ Դուք գծում եք հատակագիծը, կահավորում սենյակները 3D-ով '
    + 'և ցանկացած տեսք վերածում ֆոտոռեալիստիկ պատկերի՝ բրաուզերում, առանց որևէ բան տեղադրելու։',
  about_body: [
    'Ամեն ինչ սկսվեց պարզ հարցից՝ ինչո՞ւ է այդքան դժվար տեսնել սենյակը նախքան դրա վրա գումար ծախսելը։ '
    + 'Կահույքը թանկ է, վերանորոգումը՝ դանդաղ, իսկ խանութում կայացրած որոշումը կիսակույր որոշում է։',
    'Ուստի Tunify-ը սենյակը նախ ցույց է տալիս էկրանին։ Գծեք պատերն իրական չափերով, տեղադրեք դռներ '
    + 'ու պատուհաններ, դասավորեք կահույքը և զբոսնեք դրանով 3D-ով։ Իսկ երբ ուզենաք տեսնել ավարտուն '
    + 'տեսքը, AI-ը այն կվերարտադրի որպես լուսանկար։',
    'Դրա շուրջ կան այն երկու բաները, որոնք պետք են իրական նախագծին՝ ինտերիերի դիզայներներ, ովքեր '
    + 'այստեղ հրապարակում են իրենց աշխատանքները և ստանձնում պատվերներ, և խանութներ՝ կահույքով, '
    + 'նյութերով ու հարդարանքով, որոնք դուք հենց նոր տեղադրեցիք ձեր հատակագծում։',
  ],
  about_contact_title: 'Կապ մեզ հետ',
  about_contact_sub: 'Հարցեր, խնդիրներ կամ համագործակցություն — զանգահարեք ցանկացած համարով։',

  ai_title: 'AI պատկերների մասին',
  ai_body:
    'AI-ով պատկերների ստեղծումը կանխատեսելի չէ և միշտ չէ, որ ճիշտ արդյունք է տալիս։ Պատկերը կարող է '
    + 'փոխել այն մանրամասները, որոնք ուզում էիք պահպանել, սխալ մեկնաբանել նյութը կամ պարզապես սխալ '
    + 'տեսք ունենալ, և նույն կարգավորումները երկրորդ փորձի ժամանակ կտան այլ արդյունք։ Դիտարկեք '
    + 'յուրաքանչյուր պատկեր որպես նախագծի տպավորություն, ոչ թե դրա բնութագիր։ Մի պատվիրեք նյութեր, '
    + 'մի կտրեք որևէ բան և մի հաստատեք աշխատանքը միայն AI պատկերի հիման վրա. ստուգեք չափերը ձեր '
    + 'հատակագծում և հարդարանքը՝ իրական նմուշներով։',
  ai_short: 'AI պատկերները մոտավոր են։ Արդյունքը փոխվում է փորձից փորձ, մանրամասները կարող են սխալ ստացվել — ստուգեք չափերը ձեր հատակագծում։',

  privacy_title: 'Գաղտնիության քաղաքականություն',
  privacy_intro:
    'Այստեղ նկարագրված է, թե ինչ տվյալներ է հավաքում Tunify-ը, ինչու և ով ուրիշ է դրանք տեսնում։ '
    + 'Սա նկարագրում է, թե ինչպես է արտադրանքն իրականում աշխատում, և ոչ թե վերապատմում օրենքը։',
  privacy_sections: [
    ['Ինչ ենք հավաքում',
      'Ձեր հաշվի տվյալները՝ email հասցե, հեռախոսահամար կամ Google հաշիվ՝ կախված գրանցման եղանակից, '
      + 'ինչպես նաև ձեր ընտրած անունն ու պրոֆիլի նկարը։ Եթե հրապարակվում եք որպես դիզայներ կամ '
      + 'խանութ, պահում ենք նաև ձեր հանրային պրոֆիլի բովանդակությունը՝ նկարագրություն, '
      + 'գտնվելու վայր, ոճեր, կոնտակտներ և գներ։'],
    ['Ինչ եք ստեղծում',
      'Հատակագծերը, 3D նախագծերը, վերբեռնված լուսանկարները, ապրանքները, մեկնաբանություններն ու '
      + 'հաղորդագրությունները պահվում են, որպեսզի կարողանաք վերադառնալ դրանց։ Այն ամենը, ինչ '
      + 'հրապարակում կամ ուղարկում եք հղումով, տեսանելի է ուրիշներին. մնացածը՝ միայն ձեզ։'],
    ['Հաղորդագրություններ',
      'Նամակագրությունը պահվում է, որպեսզի երկու կողմն էլ կարողանա այն կարդալ հետո։ '
      + 'Հաղորդագրության տեքստը ուղարկելուց առաջ ավտոմատ ստուգվում է արգելված բառերի ցանկով. '
      + 'ստուգումը կատարվում է ձեր բրաուզերում, և հաղորդագրությունը դրա համար ոչ մի տեղ չի ուղարկվում։'],
    ['AI պատկերների ստեղծում',
      'Պատկեր ստեղծելիս ելակետային պատկերը և ձեր ընտրած կարգավորումներն ուղարկվում են Replicate, '
      + 'որը գործարկում է մոդելը և վերադարձնում արդյունքը։ Մի վերբեռնեք պատկերներ, որոնք պատրաստ '
      + 'չեք մշակել երրորդ կողմի ծառայությունում։'],
    ['Ով է մշակում տվյալները',
      'Google Firebase-ը ապահովում է նույնականացումը, տվյալների բազան, ֆայլերի պահոցը և push-ծանուցումները։ '
      + 'Replicate-ը գործարկում է AI մոդելները։ Google reCAPTCHA-ն ստուգում է հեռախոսով մուտքը։ '
      + 'Նրանցից յուրաքանչյուրը ստանում է միայն այն, ինչ անհրաժեշտ է իր խնդրի համար։'],
    ['Ծանուցումներ',
      'Push-ծանուցումներն ուղարկվում են միայն այն բանից հետո, երբ դուք թույլ եք տալիս դրանք '
      + 'բրաուզերում, և կարող եք ցանկացած պահի հետ վերցնել այդ թույլտվությունը բրաուզերի կարգավորումներից։'],
    ['Ինչ չենք անում',
      'Մենք չենք վաճառում ձեր անձնական տվյալները և չենք օգտագործում երրորդ կողմի գովազդային հետագծիչներ։'],
    ['Ձեր հնարավորությունները',
      'Կարող եք խմբագրել կամ ջնջել ձեր նախագծերը, ապրանքներն ու հրապարակված աշխատանքները հենց '
      + 'հավելվածում, ինչպես նաև փոխել կամ թաքցնել ձեր հանրային պրոֆիլը։ Հաշիվը և դրա տվյալները '
      + 'ջնջելու համար զանգահարեք ներքևի համարներից որևէ մեկով։'],
    ['Փոփոխություններ',
      'Էական փոփոխությունների դեպքում մենք կթարմացնենք այս էջը։ Փոփոխությունից հետո Tunify-ից '
      + 'շարունակ օգտվելը նշանակում է, որ ընդունում եք գործող տարբերակը։'],
  ],
  privacy_contact: 'Հարցե՞ր ունեք։ Զանգահարեք մեզ՝',
  legal_reviewed_note:
    'Այս էջը նկարագրում է, թե ինչպես է Tunify-ը վարվում տվյալների հետ։ Սա իրավաբանական '
    + 'խորհրդատվություն չէ և չի ստուգվել իրավաբանի կողմից։',
};

const LOCALES = { en, ru, hy };

/** Long-form copy for the active language, falling back to English. */
export function legalCopy(lang) {
  return LOCALES[lang] || LOCALES.en;
}

export default legalCopy;
